import { messageServiceBaseURL } from '@/backend/messageServiceApi'
import { buildMessageServiceWsURL, parseNotificationPayload } from '@/components/notifications/notificationWsLogic.mjs'

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 15000

/**
 * Менеджер WebSocket к message-service /api/ws.
 * onNotification(payload) вызывается на каждое валидное уведомление.
 */
export function createMessageServiceSocket({ getToken, onNotification, onStatus } = {}) {
  let socket = null
  let closedByUser = false
  let reconnectAttempt = 0
  let reconnectTimer = null
  /** Инкремент при каждом connect/disconnect — onclose старого сокета не должен reconnect. */
  let generation = 0

  function status(s) {
    if (typeof onStatus === 'function') onStatus(s)
  }

  function clearReconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function scheduleReconnect(gen) {
    if (closedByUser || gen !== generation) return
    clearReconnect()
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** reconnectAttempt))
    reconnectAttempt += 1
    reconnectTimer = setTimeout(() => {
      if (closedByUser || gen !== generation) return
      connect()
    }, delay)
  }

  function connect() {
    clearReconnect()
    const token = typeof getToken === 'function' ? getToken() : ''
    const url = buildMessageServiceWsURL(messageServiceBaseURL(), token)
    if (!url) {
      status('idle')
      return
    }

    const gen = ++generation
    closedByUser = false

    if (socket) {
      const prev = socket
      socket = null
      try {
        prev.onclose = null
        prev.onerror = null
        prev.onmessage = null
        prev.close()
      } catch (_) { /* ignore */ }
    }

    status('connecting')
    const ws = new WebSocket(url)
    socket = ws

    ws.onopen = () => {
      if (gen !== generation) return
      reconnectAttempt = 0
      status('open')
    }

    ws.onmessage = (event) => {
      if (gen !== generation) return
      const payload = parseNotificationPayload(event.data)
      if (payload && typeof onNotification === 'function') {
        onNotification(payload)
      }
    }

    ws.onerror = () => {
      if (gen !== generation) return
      status('error')
    }

    ws.onclose = () => {
      if (gen !== generation) return
      if (socket === ws) socket = null
      status('closed')
      scheduleReconnect(gen)
    }
  }

  function disconnect() {
    closedByUser = true
    generation += 1
    clearReconnect()
    if (socket) {
      const prev = socket
      socket = null
      try {
        prev.onclose = null
        prev.onerror = null
        prev.onmessage = null
        prev.close()
      } catch (_) { /* ignore */ }
    }
    status('idle')
  }

  return { connect, disconnect }
}
